import React from 'react';
import { makePrioStyles } from '../../../theme/utils';
import Flex from '../../../components/Flex';
import { useTheme } from '@prio365/prio365-react-library';
import { PrioTheme } from '../../../theme/types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { getMailSettings } from '../../../apps/main/rootReducer';

const useStyles = makePrioStyles((theme) => ({
  '@keyframes shimmer': {
    from: { transform: 'translateX(0)' },
    to: { transform: 'translateX(200%)' },
  },

  skeleton: {
    height: '100%',
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: `color-mix(
      in srgb,
      ${theme.colors.base.blue[50]} 30%,
      transparent 70%
    )`,
  },
  loadingAnimation: {
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-150%',
      width: '150%',
      height: '100%',
      background:
        'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.4) 50%, rgba(255,255,255,0) 100%)',
      animation: '$shimmer 2s ease-in-out infinite',
    },
  },
  circle: {
    borderRadius: '50%',
    width: 24,
    height: 24,
  },
}));

interface MailListSkeletonProps {}

export const MailListSkeleton: React.FC<MailListSkeletonProps> = (
  props: MailListSkeletonProps
) => {
  //#region ------------------------------ Defaults
  const theme = useTheme<PrioTheme>();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const mailSettings = useSelector(getMailSettings);

  const verticalHeight =
    mailSettings.mailListSpacing === 'tight'
      ? 63
      : mailSettings.mailListSpacing === 'middle'
        ? 73
        : 81;

  const horizontalHeight =
    mailSettings.mailListSpacing === 'tight'
      ? 28
      : mailSettings.mailListSpacing === 'middle'
        ? 32
        : 44;

  const padding =
    mailSettings.mailListSpacing === 'full' ? 10 : theme.spacing.small;
  //#endregion

  const renderVerticalItem = (index: number) => {
    return (
      <Flex.Row key={index} height={verticalHeight} padding={padding}>
        <Flex.Column
          flex={1}
          alignItems="center"
          justifyContent="center"
        ></Flex.Column>
        <Flex.Column childrenGap={theme.spacing.small} flex={5}>
          <Flex.Row flex={1} childrenGap={theme.spacing.large}>
            <Flex.Item flex={3}>
              <Skeleton />
            </Flex.Item>
            <Flex.Item flex={1}>
              <Skeleton />
            </Flex.Item>
          </Flex.Row>
          <Flex.Row flex={1}>
            <Flex.Item flex={3}>
              <Skeleton />
            </Flex.Item>
            <Flex.Item flex={1}></Flex.Item>
          </Flex.Row>
          <Flex.Row flex={1} childrenGap={100}>
            <Flex.Item flex={1}>
              <Skeleton />
            </Flex.Item>
            <Flex.Item flex={4}>
              <Skeleton />
            </Flex.Item>
          </Flex.Row>
        </Flex.Column>
      </Flex.Row>
    );
  };

  const renderHorizontalItem = (index: number) => {
    const randomNumbers = [9, 3, 6];
    const randomNumber = randomNumbers[index];
    return (
      <Flex.Row
        key={index}
        childrenGap={theme.spacing.regular}
        height={horizontalHeight}
        padding={padding}
      >
        <Flex.Row flex={2} paddingRight={70} paddingLeft={40}>
          <Flex.Item flex={30 - randomNumber / 2}>
            <Skeleton />
          </Flex.Item>
          <Flex.Item flex={10 + randomNumber / 2} />
        </Flex.Row>
        <Flex.Row flex={5} childrenGap={theme.spacing.regular}>
          <Flex.Item flex={20 - randomNumber}>
            <Skeleton />
          </Flex.Item>
          <Flex.Item flex={20 + randomNumber}>
            <Skeleton />
          </Flex.Item>
        </Flex.Row>
        <Flex.Item flex={1} paddingLeft={70} paddingRight={150}>
          <Skeleton />
        </Flex.Item>
      </Flex.Row>
    );
  };
  return (
    <div>
      {Array(3)
        .fill(null)
        .map((_, index) =>
          mailSettings.messageViewLayout === 'right'
            ? renderVerticalItem(index)
            : renderHorizontalItem(index)
        )}
    </div>
  );
};

export default MailListSkeleton;

interface SkeletonProps {
  className?: string;
  showAnimation?: boolean;
  isCircle?: boolean;
}

export const Skeleton: React.FC<SkeletonProps> = (props) => {
  //#region ------------------------------ Defaults
  const { className, showAnimation = true, isCircle = false } = props;
  const classes = useStyles();
  //#endregion

  return (
    <div
      className={classNames(
        classes.skeleton,
        {
          [classes.loadingAnimation]: showAnimation,
          [classes.circle]: isCircle,
        },
        className
      )}
    ></div>
  );
};
